import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../index'

export interface CheckAnalyticsSummary {
  totalVisitors: number
  totalVisitorsAll: number
  landingVisitors: number
  landingVisitorsAll: number
  /** Distinct IPs for mo_account_registered in range */
  accountsRegistered: number
  /** Total rows for mo_account_registered in range */
  accountsRegisteredAll: number
  slackConnected: number
  slackConnectedAll: number
  githubConnected: number
  githubConnectedAll: number
  jiraConnected: number
  jiraConnectedAll: number
  azureDevopsConnected: number
  azureDevopsConnectedAll: number
  bitbucketConnected: number
  bitbucketConnectedAll: number
  linearConnected: number
  linearConnectedAll: number
  creditsPurchased: number
  creditsPurchasedAll: number
  avgDurationSeconds: number | null
  totalActivityEvents: number
  avgActionsPerSession: number
}

export interface DailyTrendRow {
  date: string
  visitor_count: number
}

export interface DeviceRow {
  device: string
  count: number
}

export interface LocationRow {
  country: string
  count: number
}

export interface CityRow {
  city: string
  region: string
  country: string
  count: number
}

export interface RegionRow {
  region: string
  country: string
  count: number
}

export interface SourceRow {
  source: string
  count: number
}

export interface MoToolPrefStatRow {
  key: string
  label: string
  total: number
  unique: number
}

export interface PortalToolConnectionRow {
  key: string
  label: string
  totalEvents: number
  uniqueUsers: number
}

export interface PrMrCompletedByProviderRow {
  provider: string
  label: string
  count: number
}

export interface DecisionGenerationStatRow {
  toolKey: string
  toolLabel: string
  decisionType: string
  category: string
  count: number
}

export interface ActionStatRow {
  action: string
  label: string
  total_occurrences: number
  unique_users: number
}

export interface StepRow {
  action: string
  label: string
  session_count: number
}

export interface ClientErrorRow {
  id: string
  kind: 'logged_in' | 'landing'
  created_at: string
  user_id: number | null
  user_email: string | null
  message: string
  details: string | null
  /** Portal section (metadata.view, pathname, …) — from API; use when details JSON was truncated. */
  where_section?: string | null
  ip_address: string | null
}

export interface CheckAnalyticsData {
  dataRange: { firstDate: string | null; lastDate: string | null }
  summary: CheckAnalyticsSummary
  dailyTrend: DailyTrendRow[]
  dailyTrendAll: DailyTrendRow[]
  landingDailyTrend: DailyTrendRow[]
  landingDailyTrendAll: DailyTrendRow[]
  deviceBreakdown: DeviceRow[]
  locationBreakdown: LocationRow[]
  cityBreakdown: CityRow[]
  regionBreakdown: RegionRow[]
  sourceBreakdown: SourceRow[]
  demoPageDecisionSources: MoToolPrefStatRow[]
  demoPageCodeHosts: MoToolPrefStatRow[]
  signupDecisionSources: MoToolPrefStatRow[]
  signupCodeHosts: MoToolPrefStatRow[]
  signupFromDemoHtml: number
  signupFromDemoHtmlAll: number
  portalToolConnections: PortalToolConnectionRow[]
  portalTrackerSourceConnections: MoToolPrefStatRow[]
  prMrChecksCompleted: PrMrCompletedByProviderRow[]
  prMrChecksCompletedTotal: number
  decisionGenerationStats: DecisionGenerationStatRow[]
  actionStats: ActionStatRow[]
  firstSteps: StepRow[]
  lastSteps: StepRow[]
  clientErrors: ClientErrorRow[]
}

export const checkAnalyticsApi = createApi({
  reducerPath: 'checkAnalyticsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/admin',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['CheckAnalytics'],
  endpoints: (builder) => ({
    getCheckAnalytics: builder.query<
      { data: CheckAnalyticsData },
      { dateFrom?: string; dateTo?: string }
    >({
      query: (params) => ({
        url: '/check-analytics',
        params,
      }),
      providesTags: ['CheckAnalytics'],
    }),
    deleteCheckClientErrors: builder.mutation<
      { success: boolean; deleted: number },
      { ids: string[] }
    >({
      query: (body) => ({
        url: '/check-analytics/client-errors/delete',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CheckAnalytics'],
    }),
  }),
})

export const { useGetCheckAnalyticsQuery, useDeleteCheckClientErrorsMutation } = checkAnalyticsApi
